import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

import { JsonAdvancedSearchResult, NodeServiceResourceService } from '@psnb/nwt-api';
import { GeneratorSearch } from '@psnb/psnb-api';
import { CommunityServedService, GeneratorService, isDefined, isDefinedNotNull } from '@psnb/services';

import { CommonService } from '../../common/common.service';
import { CreateAssetDialogComponent } from '../create-asset-dialog/create-asset-dialog.component';
import { GeneratorSearchListViewModel } from './generator-searchlist.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'psnb-power-supply-generator-searchlist',
  styleUrls: ['./power-supply-generator-searchlist.component.scss'],
  templateUrl: './power-supply-generator-searchlist.component.html'
})
export class PowerSupplyGeneratorSearchlistComponent implements OnInit, AfterViewInit, OnDestroy {
  public vm: GeneratorSearchListViewModel;

  // ViewChild references
  @ViewChild('assetIdInput', { read: MatAutocompleteTrigger }) private assetIdAutocompleteTrigger?: MatAutocompleteTrigger;
  @ViewChild('focusFrame', { read: ElementRef }) private focusFrameElement?: ElementRef<HTMLElement>;
  @ViewChild('scanner', { read: ElementRef }) private scannerElement?: ElementRef<HTMLElement>;

  // Private, readonly variables
  private readonly DEBOUNCE_TIME_IN_MS: number = 1000;

  // Private variables
  private destroyed$: Subject<void>;
  private isScannedSearch: boolean;
  private psCreateAssetDialogComponentRef: MatDialogRef<CreateAssetDialogComponent> | undefined;
  private userpermissions: Array<{ category: string; functionality: string; permission: string }> | undefined;

  public constructor(
    private cdr: ChangeDetectorRef,
    private communityServedService: CommunityServedService,
    private dialog: MatDialog,
    private generatorService: GeneratorService,
    private nodeService: NodeServiceResourceService,
    private router: Router
  ) {
    this.destroyed$ = new Subject<void>();
    this.isScannedSearch = false;
  }

  // Getters/Setters
  public getAssetId(option: GeneratorSearch | string): string {
    return typeof option === 'string' ? option : option?.assetId ?? '';
  }
  public getMake: (option: GeneratorSearch) => string | undefined =
    (option: GeneratorSearch): string | undefined => option?.generatorManufacturer;
  public getModel: (option: GeneratorSearch) => string | undefined =
    (option: GeneratorSearch): string | undefined => option?.generatorModel;
  public getNodeName: (option: { node?: string; nodeCmts?: string }) => string | undefined = (option: {
    node?: string;
    nodeCmts?: string;
  }): string | undefined => option ? `${option.node} / ${option.nodeCmts}` : undefined;
  public getNwtTeam: (option: GeneratorSearch) => string | undefined =
    (option: GeneratorSearch): string | undefined => option?.teamName;

  public ngOnInit(): void {
    this.vm = {
      assetIDSearchTerm: new FormControl(),
      createEnable: undefined,
      generatorAssetSearchResult: [],
      generatorMakeSearchResult: [],
      generatorModelSearchResult: [],
      generatorNwtTeamSearchResult: [],
      makeSearchTerm: new FormControl(),
      modelSearchTerm: new FormControl(),
      nodeSearchTerm: new FormControl(),
      nodeValue: '',
      nwtTeamSearchTerm: new FormControl(),
      scannedValue: '',
      scannerEnabled: false,
      searchNodeFlag: true,
      searchResult: [],
      zoomFlag: false,
      zoomLevel: 1
    };

    this.setupSearchSubscription(
      this.vm.assetIDSearchTerm,
      (assetIdSearchData: string): void => {
        this.generatorService
          .advanceSearchByAssetID(assetIdSearchData)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((response: GeneratorSearch[]): void => {
            this.vm.generatorAssetSearchResult = response;
            this.cdr.detectChanges();
            if (this.isScannedSearch &&
                isDefined(this.assetIdAutocompleteTrigger) &&
                response.length > 0) {
              this.assetIdAutocompleteTrigger.openPanel();
              this.isScannedSearch = false;
            }
          });
      }
    );

    this.setupSearchSubscription(
      this.vm.makeSearchTerm,
      (makeSearchData: string): void => {
        this.generatorService
          .getMakeOptions(makeSearchData)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((response: string[]): void => {
            this.vm.generatorMakeSearchResult = response;
            this.cdr.detectChanges();
          });
      }
    );

    this.setupSearchSubscription(
      this.vm.modelSearchTerm,
      (modelSearchData: string): void => {
        this.generatorService
          .getModelOptions(modelSearchData)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((response: string[]): void => {
            this.vm.generatorModelSearchResult = response;
            this.cdr.detectChanges();
          });
      }
    );

    this.setupSearchSubscription(
      this.vm.nwtTeamSearchTerm,
      (nwtTeamSearchData: string): void => {
        this.generatorService
          .getNwtTeamNameOptions(nwtTeamSearchData)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((response: string[]): void => {
            this.vm.generatorNwtTeamSearchResult = response;
            this.cdr.detectChanges();
          });
      }
    );

    this.vm.nodeSearchTerm.valueChanges
      .pipe(
        debounceTime(this.DEBOUNCE_TIME_IN_MS),
        takeUntil(this.destroyed$)
      )
      .subscribe((nodeSearchData: string): void => {
        const searchTerm: string = typeof nodeSearchData === 'string' ? nodeSearchData.trim() : '';
        this.vm.searchResult = searchTerm ? [] : [];
        this.cdr.detectChanges();
        if (isDefinedNotNull(searchTerm)) {
          this.searchWord(searchTerm)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((response: JsonAdvancedSearchResult[]): void => {
              this.vm.searchResult = response;
              this.cdr.detectChanges();
            });
        }
      });
    }

  public ngOnDestroy(): void {
    this.psCreateAssetDialogComponentRef?.close();
    this.destroyed$?.next();
    this.destroyed$?.complete();
  }

  public ngAfterViewInit(): void {
    //Permission Check
    this.userpermissions = JSON.parse(localStorage.getItem('user-permissions'));
    for (const element of this.userpermissions) {
      if (
        element.category === CommonService.GENERATOR_ADMIN_ONLY &&
        element.functionality === CommonService.CREATE_GENERATOR
      ) {
        this.vm.createEnable = this.permissionlevel(element.permission);
      }
    }
  }

  private permissionlevel(level: string): number {
    switch (level) {
      case CommonService.ENABLED:
        return 0;
      case CommonService.DISABLED:
        return 1;
      case CommonService.READONLY:
        return 2;
      default:
        return 2;
    }
  }

  // Public methods
  /**
   * Handles node selection from autocomplete.
   * Updates the community served service and navigates to asset search with selected node details.
   *
   * @param {MatAutocompleteSelectedEvent} selectedNodeEvent - The autocomplete selection event containing the
   * selected node data
   */
  public nodeSelected(selectedNodeEvent: MatAutocompleteSelectedEvent): void {
    const selectedValue: {
      node?: string;
      nodeCmts?: string;
      nodeCommunintyServed?: string;
      nodeId?: string | number;
    } = selectedNodeEvent.option.value;
    selectedNodeEvent.option.value = `${selectedValue.node} / ${selectedValue.nodeCmts}`;
    this.communityServedService.communityServed = selectedValue.nodeCommunintyServed;
    this.navigateToAssetSearch({
      node: selectedValue.node,
      nodeCommunintyServed: selectedValue.nodeCommunintyServed,
      nodeId: selectedValue.nodeId
    });
  }

  /**
   * Handles node search input changes.
   * Updates the node value and search flag based on input length.
   *
   * @param {Event} event - The input event from the search field
   */
  public onChangeSearchNodes(event: Event): void {
    const target: EventTarget | null = event.target;
    if (target instanceof HTMLInputElement) {
      this.vm.nodeValue = target.value;
      this.vm.searchNodeFlag = this.vm.nodeValue.length < 4;
      this.cdr.detectChanges();
    }
  }

  /**
   * Handles navigation after node search.
   * Searches for the selected node in results and navigates to asset search.
   */
  public onChangeSearchPSNav(): void {
    const selectedNode: {
      node?: string;
      nodeCmts?: string;
      nodeCommunintyServed?: string;
      nodeId?: string | number;
    } | undefined = this.vm.searchResult.find((item: {
      node?: string;
      nodeCmts?: string;
      nodeCommunintyServed?: string;
      nodeId?: string | number;
    }): boolean =>
      item.node === this.vm.nodeValue || `${item.node} / ${item.nodeCmts}` === this.vm.nodeValue
    );

    if (isDefined(selectedNode)) {
      this.communityServedService.communityServed = selectedNode.nodeCommunintyServed;
      this.navigateToAssetSearch({
        node: selectedNode.node,
        nodeCommunintyServed: selectedNode.nodeCommunintyServed,
        nodeId: selectedNode.nodeId
      });
    } else {
      this.navigateToAssetSearch({ nodeText: this.vm.nodeValue });
    }
  }

  /**
   * Searches for generators by asset ID.
   * Handles QR code asset ID, fuel tank ID, trailer ID, or generator ID.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event containing the asset data
   */
  public searchByAssetId(event: MatAutocompleteSelectedEvent): void {
    const referenceId: string =
      event.option.value.fuelTankId ||
      event.option.value.trailerId ||
      event.option.value.generatorId;
    const qrCodeAssetId: string = event.option.value.assetId;
    this.navigateToAssetSearch({ assetId: qrCodeAssetId ?? referenceId });
  }

  /**
   * Navigates to the generator hierarchy search page.
   */
  public searchByGeneratorHierarchy(): void {
    this.router.navigate(['/list'], { queryParams: { type: 'SearchHierarchy' } });
  }

  /**
   * Opens the create asset dialog.
   */
  public openCreateAssetDialog(): void {
    if (this.psCreateAssetDialogComponentRef?.getState() === MatDialogState.OPEN) {
      return;
    }
    this.psCreateAssetDialogComponentRef = this.dialog.open(CreateAssetDialogComponent, {
      disableClose: true,
      height: '250px',
      panelClass: 'my-centered-dialog',
      width: '512px'
    });
  }

  /**
   * Searches for generators by make.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event containing the selected make
   */
  public searchByMake(event: MatAutocompleteSelectedEvent): void {
    this.navigateToAssetSearch({ make: event.option.value });
  }

  /**
   * Searches for generators by model.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event containing the selected model
   */
  public searchByModel(event: MatAutocompleteSelectedEvent): void {
    this.navigateToAssetSearch({ model: event.option.value });
  }

  /**
   * Searches for generators by NWT team.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event containing the selected NWT team
   */
  public searchByNwTTeam(event: MatAutocompleteSelectedEvent): void {
    this.navigateToAssetSearch({ nwtTeam: event.option.value });
  }

  // Private methods
  private navigateToAssetSearch(queryParams: {
    assetId?: string;
    make?: string;
    model?: string;
    node?: string;
    nodeCommunintyServed?: string;
    nodeId?: string | number;
    nodeText?: string;
    nwtTeam?: string;
  }): void {
    this.router.navigate(['/assetsearchdata'], { queryParams });
  }

  private searchWord(term: string): Observable<JsonAdvancedSearchResult[]> {
    return this.nodeService.resourceNodeServiceResourceAdvancedSearchGET(term);
  }

  private setupSearchSubscription(
    control: FormControl,
    searchFn: (searchSubscriptionData: string) => void
  ): void {
    control.valueChanges
      .pipe(debounceTime(this.DEBOUNCE_TIME_IN_MS), takeUntil(this.destroyed$))
      .subscribe((searchSubscriptionData: string): void => {
        if (searchSubscriptionData !== '') {
          searchFn(searchSubscriptionData);
        }
      });
  }

  public enableScanner(): void {
    if (this.vm.scannerEnabled) {
      this.vm.zoomFlag = false;
      this.vm.scannerEnabled = false;
    } else {
      this.vm.assetIDSearchTerm.setValue('');
      this.vm.generatorAssetSearchResult = [];
      this.vm.scannedValue = '';
      this.vm.zoomLevel = 1;
      this.vm.zoomFlag = true;
      this.vm.scannerEnabled = true;
    }
    this.cdr.detectChanges();
  }

  public scanSuccessHandler(event: string): void {
    this.vm.scannedValue = event;
    this.vm.scannerEnabled = false;
    this.vm.zoomFlag = false;
    this.isScannedSearch = true;
    this.vm.assetIDSearchTerm.setValue(event);
    this.cdr.detectChanges();
  }

  public zoomIn(): void {
    if (this.vm.zoomLevel < 1.4) {
      this.vm.zoomLevel = Math.round((this.vm.zoomLevel + 0.1) * 10) / 10;
      this.applyZoom();
    }
  }

  public zoomOut(): void {
    if (this.vm.zoomLevel > 1) {
      this.vm.zoomLevel = Math.round((this.vm.zoomLevel - 0.1) * 10) / 10;
      this.applyZoom();
    }
  }

  private applyZoom(): void {
    if (isDefined(this.scannerElement?.nativeElement)) {
      this.scannerElement.nativeElement.style.transform = `scale(${this.vm.zoomLevel})`;
    }
    if (isDefined(this.focusFrameElement?.nativeElement)) {
      this.focusFrameElement.nativeElement.style.transform = `translate(-50%, -50%) scale(${this.vm.zoomLevel})`;
    }
  }

  /**
   * TrackBy function for asset list optimization.
   *
   * @param {number} index - The index of the item in the list
   * @param {GeneratorSearch} asset - The asset object
   * @returns {string | undefined} The unique asset ID
   */
  public trackByAsset(index: number, asset: GeneratorSearch): string | undefined {
    return asset?.assetId;
  }

  /**
   * TrackBy function for NWT team list optimization.
   *
   * @param {number} index - The index of the item in the list
   * @param {GeneratorSearch} nwtTeam - The NWT team object
   * @returns {string | undefined} The unique team name
   */
  public trackByNwtTeam(index: number, nwtTeam: GeneratorSearch): string | undefined {
    return nwtTeam?.teamName;
  }

  /**
   * TrackBy function for node list optimization.
   *
   * @param {number} index - The index of the item in the list
   * @param {{ node?: string; nodeCmts?: string }} node - The node object containing node and nodeCmts
   * @returns {string | undefined} The unique node identifier
   */
  public trackByNode(index: number, node: { node?: string; nodeCmts?: string }): string | undefined {
    return node?.node;
  }

  /**
   * TrackBy function for make list optimization.
   *
   * @param {number} index - The index of the item in the list
   * @param {GeneratorSearch} make - The make object
   * @returns {string | undefined} The unique manufacturer name
   */
  public trackByMake(index: number, make: GeneratorSearch): string | undefined {
    return make?.generatorManufacturer;
  }

  /**
   * TrackBy function for model list optimization.
   *
   * @param {number} index - The index of the item in the list
   * @param {GeneratorSearch} model - The model object
   * @returns {string | undefined} The unique model name
   */
  public trackByModel(index: number, model: GeneratorSearch): string | undefined {
    return model?.generatorModel;
  }
}
